// src/pages/analytics/MarginAnalysisTab.tsx
//
// Margins (recipe margin-analysis, flag analytics.margins, agent 3.22.0+).
// GP % by a chosen dimension against a target %, the change vs the prior
// period of the same length in points (erosion ≤ −5 pts highlighted), and the
// invoice lines sold below cost. Costs are line costs at time of sale.
// Margin % / Markup % / Both toggle (./marginMode); the target applies to margin %.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TrendingDown, RefreshCw, Download, FileSpreadsheet, RotateCcw, AlertTriangle } from 'lucide-react';
import { apiV2 as api } from '../../services/apiV2';
import DateField from '../../components/DateField';
import type { MarginAnalysisResponse, MarginBelowCostLine, MarginRow } from '../../types';
import { exportCsv, type CsvColumns } from '../../lib/csvExport';
import { exportXlsx } from '../../lib/xlsxExport';
import { FilterPane, AnalyticsSavedViews } from './filterPane';
import { useFilterPane } from './filterPaneState';
import { PctModeToggle, usePctMode, markupPct, showMargin, showMarkup } from './marginMode';

const fmtCur = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
const fmtCur2 = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
const fmtNum = (v: number) => new Intl.NumberFormat('en-US').format(Math.round(v));

const DIMENSIONS: { value: string; label: string }[] = [
  { value: 'itemCategory',    label: 'Item Category' },
  { value: 'itemSubcategory', label: 'Item Subcategory' },
  { value: 'item',            label: 'Item' },
  { value: 'customer',        label: 'Customer' },
  { value: 'salesman',        label: 'Salesman' },
  { value: 'priceLevel',      label: 'Price Level' },
];
const DEFAULT_TARGET = 20;
const EROSION_PTS = -5;

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function needsNewerAgent(err: unknown): boolean {
  return /404|unknown recipe|not found/i.test((err as Error)?.message ?? '');
}

function PctVsTarget({ pct, target }: { pct: number; target: number }) {
  const cls = pct < 0 ? 'text-red-400' : pct < target ? 'text-amber-400' : 'text-emerald-400';
  return <span className={`tabular-nums font-medium ${cls}`}>{pct.toFixed(1)}%</span>;
}

function MarkupPct({ gp, cost }: { gp: number; cost: number }) {
  const m = markupPct(gp, cost);
  if (m == null) return <span className="text-slate-600">–</span>;
  return <span className={`tabular-nums font-medium ${m < 0 ? 'text-red-400' : 'text-slate-200'}`}>{m.toFixed(1)}%</span>;
}

function DeltaPts({ pts }: { pts: number | null }) {
  if (pts == null) return <span className="text-slate-600">–</span>;
  const cls = pts <= EROSION_PTS ? 'text-red-400 font-semibold' : pts < 0 ? 'text-amber-300' : pts > 0 ? 'text-emerald-400' : 'text-slate-400';
  return <span className={`tabular-nums ${cls}`}>{pts > 0 ? '+' : ''}{pts.toFixed(1)} pts</span>;
}

const ExportButtons = ({ onCsv, onXlsx, disabled, what }: { onCsv: () => void; onXlsx: () => void; disabled: boolean; what: string }) => (
  <>
    <button onClick={onCsv} disabled={disabled} title={`Export ${what} to CSV`}
      className="px-2 py-1 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
      <Download size={11} /> CSV
    </button>
    <button onClick={onXlsx} disabled={disabled} title={`Export ${what} to Excel`}
      className="px-2 py-1 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
      <FileSpreadsheet size={11} /> Excel
    </button>
  </>
);

const BELOW_COLS: CsvColumns<MarginBelowCostLine> = [
  ['Invoice',   (r) => r.invoice],
  ['Date',      (r) => r.date],
  ['Code',      (r) => r.customerCode],
  ['Customer',  (r) => r.customerName],
  ['Salesman',  (r) => r.salesman],
  ['Item',      (r) => r.itemNo],
  ['Description', (r) => r.itemDesc],
  ['Qty',       (r) => r.qty],
  ['Price',     (r) => r.price],
  ['Unit Cost', (r) => r.unitCost],
  ['Ext',       (r) => r.ext],
  ['GP',        (r) => r.gp],
  ['GP %',      (r) => r.gpPct],
];

export function MarginAnalysisTab() {
  const [dimension, setDimension] = useState('itemCategory');
  const [dateFrom, setDateFrom] = useState(() => isoDaysAgo(90));
  const [dateTo, setDateTo] = useState(() => isoDaysAgo(0));
  const [targetPct, setTargetPct] = useState(DEFAULT_TARGET);
  const [pctMode, setPctMode] = usePctMode();
  const resetFilters = () => {
    setDimension('itemCategory'); setDateFrom(isoDaysAgo(90)); setDateTo(isoDaysAgo(0)); setTargetPct(DEFAULT_TARGET);
  };

  const pane = useFilterPane();
  const q = useQuery({
    queryKey: ['analyticsMargins', dimension, dateFrom, dateTo, targetPct, pane.key],
    queryFn: () => api.getMarginAnalysis({ dimension, dateFrom, dateTo, targetPct }, pane.params),
    staleTime: 60_000,
    retry: (n, err) => !needsNewerAgent(err) && n < 1,
  });
  const data: MarginAnalysisResponse | undefined = q.data;
  const t = data?.totals;
  const target = data?.targetPct ?? targetPct;
  const dimensionLabel = DIMENSIONS.find(d => d.value === dimension)?.label ?? dimension;

  const rowCols: CsvColumns<MarginRow> = [
    [dimensionLabel, (r) => r.label],
    ['Qty',          (r) => r.qty],
    ['Sales',        (r) => r.sales],
    ['Cost',         (r) => r.cost],
    ['GP',           (r) => r.gp],
    ...(showMargin(pctMode) ? ([['Margin %', (r: MarginRow) => r.gpPct]] as CsvColumns<MarginRow>) : []),
    ...(showMarkup(pctMode) ? ([['Markup %', (r: MarginRow) => { const m = markupPct(r.gp, r.cost); return m == null ? '' : Math.round(m * 10) / 10; }]] as CsvColumns<MarginRow>) : []),
    ['Prior Sales',  (r) => r.prevSales],
    ['Prior GP %',   (r) => r.prevGpPct],
    ['Change (pts)', (r) => r.deltaPts ?? ''],
    ['Lines below cost',   (r) => r.belowCostLines],
    ['Lines below target', (r) => r.belowTargetLines],
  ];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <TrendingDown size={20} /> Margins
        </h2>
        <p className="text-sm text-slate-400 mt-0.5">
          GP % by {dimensionLabel.toLowerCase()} against a {target}% target, and what sold below cost
          {data && <span className="text-slate-500"> · {data.dateFrom} → {data.dateTo} vs {data.prevFrom} → {data.prevTo}</span>}
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">By</span>
          <select value={dimension} onChange={e => setDimension(e.target.value)}
            className="px-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50">
            {DIMENSIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </div>
        <DateField label="From" value={dateFrom} onChange={setDateFrom} />
        <DateField label="To" value={dateTo} onChange={setDateTo} />
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Target GP %</span>
          <input type="number" min={0} max={100} step={1} value={targetPct}
            onChange={e => { const v = Number(e.target.value); if (Number.isFinite(v)) setTargetPct(Math.min(100, Math.max(0, v))); }}
            className="px-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white w-20 focus:outline-none focus:ring-1 focus:ring-blue-500/50" />
        </div>
        <PctModeToggle mode={pctMode} onChange={setPctMode} />
        <div className="flex-1" />
        <AnalyticsSavedViews
          page="margin-analysis"
          getState={() => ({ dimension, dateFrom, dateTo, targetPct })}
          applyState={(s) => {
            if (typeof s.dimension === 'string' && DIMENSIONS.some(d => d.value === s.dimension)) setDimension(s.dimension);
            if (typeof s.dateFrom === 'string') setDateFrom(s.dateFrom);
            if (typeof s.dateTo === 'string') setDateTo(s.dateTo);
            if (typeof s.targetPct === 'number') setTargetPct(s.targetPct);
          }}
        />
        <button onClick={resetFilters} title="Reset all filters to defaults"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1">
          <RotateCcw size={11} /> Reset
        </button>
        <button onClick={() => q.refetch()} disabled={q.isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      <FilterPane />

      {q.error && (
        needsNewerAgent(q.error) ? (
          <div className="p-4 rounded-md bg-slate-800/40 border border-slate-700/40 text-slate-300 text-sm flex items-center gap-2">
            <AlertTriangle size={16} className="text-amber-400" /> Needs agent 3.22.0+ — update the agent to see margin analysis.
          </div>
        ) : (
          <div className="p-4 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
            <AlertTriangle size={16} />
            <div>
              <div className="font-medium">Couldn't load the margin analysis</div>
              <div className="text-xs text-red-300/70 mt-0.5">{(q.error as Error).message}</div>
            </div>
          </div>
        )
      )}

      {q.isLoading ? (
        <div className="flex items-center justify-center h-40"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : !q.error && data && t && (
        <>
          {/* KPI tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="rounded-xl border border-violet-500/30 bg-violet-500/5 p-4">
              <div className="text-xs text-slate-400 uppercase tracking-wider">{pctMode === 'markup' ? 'Markup %' : pctMode === 'both' ? 'Margin / Markup' : 'Margin %'}</div>
              <div className="text-xl font-semibold mt-1">
                {showMargin(pctMode) && <PctVsTarget pct={t.gpPct} target={target} />}
                {pctMode === 'both' && <span className="text-slate-500 mx-1.5">/</span>}
                {showMarkup(pctMode) && <MarkupPct gp={t.gp} cost={t.cost} />}
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">{fmtCur(t.gp)} on {fmtCur(t.sales)}</div>
            </div>
            <div className="rounded-xl border border-slate-700/40 bg-slate-900/40 p-4">
              <div className="text-xs text-slate-400 uppercase tracking-wider">vs prior period</div>
              <div className="text-xl font-semibold mt-1"><DeltaPts pts={t.deltaPts} /></div>
              <div className="text-[11px] text-slate-500 mt-0.5">was {t.prevGpPct.toFixed(1)}%</div>
            </div>
            <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4">
              <div className="text-xs text-slate-400 uppercase tracking-wider">Sold below cost</div>
              <div className="text-xl font-semibold text-red-300 tabular-nums mt-1">{fmtCur(t.belowCostSales)}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">{fmtNum(t.belowCostLines)} lines</div>
            </div>
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
              <div className="text-xs text-slate-400 uppercase tracking-wider">Below {target}% target</div>
              <div className="text-xl font-semibold text-amber-300 tabular-nums mt-1">{fmtCur(t.belowTargetSales)}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">{fmtNum(t.belowTargetLines)} lines</div>
            </div>
            <div className="rounded-xl border border-slate-700/40 bg-slate-900/40 p-4">
              <div className="text-xs text-slate-400 uppercase tracking-wider">No cost on file</div>
              <div className="text-xl font-semibold text-white tabular-nums mt-1">{fmtCur(t.noCostSales)}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">counted at 100% GP</div>
            </div>
            <div className="rounded-xl border border-blue-500/30 bg-blue-500/5 p-4">
              <div className="text-xs text-slate-400 uppercase tracking-wider">Sales</div>
              <div className="text-xl font-semibold text-white tabular-nums mt-1">{fmtCur(t.sales)}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">prior {fmtCur(t.prevSales)}</div>
            </div>
          </div>

          {/* By dimension */}
          <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
            <div className="flex items-center justify-between px-4 pt-3 pb-2">
              <h3 className="text-sm font-semibold text-white">By {dimensionLabel.toLowerCase()}</h3>
              <div className="flex items-center gap-2">
                <ExportButtons what="the margin table" disabled={data.rows.length === 0}
                  onCsv={() => exportCsv('margins', rowCols, data.rows)} onXlsx={() => exportXlsx('margins', rowCols, data.rows)} />
              </div>
            </div>
            <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 z-10 bg-slate-800">
                  <tr className="border-b border-slate-700/50 text-slate-400">
                    <th className="px-3 py-2.5 text-left font-medium min-w-[160px]">{dimensionLabel}</th>
                    <th className="px-3 py-2.5 text-right font-medium">Qty</th>
                    <th className="px-3 py-2.5 text-right font-medium">Sales</th>
                    <th className="px-3 py-2.5 text-right font-medium">Cost</th>
                    <th className="px-3 py-2.5 text-right font-medium">GP $</th>
                    {showMargin(pctMode) && <th className="px-3 py-2.5 text-right font-medium" title="GP ÷ sales">Margin %</th>}
                    {showMarkup(pctMode) && <th className="px-3 py-2.5 text-right font-medium" title="GP ÷ cost">Markup %</th>}
                    <th className="px-3 py-2.5 text-right font-medium">Prior margin %</th>
                    <th className="px-3 py-2.5 text-right font-medium">Change</th>
                    <th className="px-3 py-2.5 text-right font-medium">Below cost</th>
                    <th className="px-3 py-2.5 text-right font-medium">Below target</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map(r => (
                    <tr key={r.key} className={`border-b border-slate-800/50 hover:bg-slate-700/20 ${r.deltaPts != null && r.deltaPts <= EROSION_PTS ? 'bg-red-950/20' : ''}`}>
                      <td className="px-3 py-1.5 text-white max-w-[260px] truncate" title={r.label}>{r.label || '—'}</td>
                      <td className="px-3 py-1.5 text-right text-slate-300 tabular-nums">{fmtNum(r.qty)}</td>
                      <td className="px-3 py-1.5 text-right text-slate-200 tabular-nums">{fmtCur(r.sales)}</td>
                      <td className="px-3 py-1.5 text-right text-slate-400 tabular-nums">{fmtCur(r.cost)}</td>
                      <td className="px-3 py-1.5 text-right text-slate-200 tabular-nums">{fmtCur(r.gp)}</td>
                      {showMargin(pctMode) && <td className="px-3 py-1.5 text-right"><PctVsTarget pct={r.gpPct} target={target} /></td>}
                      {showMarkup(pctMode) && <td className="px-3 py-1.5 text-right"><MarkupPct gp={r.gp} cost={r.cost} /></td>}
                      <td className="px-3 py-1.5 text-right text-slate-400 tabular-nums">{r.prevSales ? `${r.prevGpPct.toFixed(1)}%` : '–'}</td>
                      <td className="px-3 py-1.5 text-right"><DeltaPts pts={r.deltaPts} /></td>
                      <td className={`px-3 py-1.5 text-right tabular-nums ${r.belowCostLines ? 'text-red-300' : 'text-slate-600'}`}>{fmtNum(r.belowCostLines)}</td>
                      <td className={`px-3 py-1.5 text-right tabular-nums ${r.belowTargetLines ? 'text-amber-300' : 'text-slate-600'}`}>{fmtNum(r.belowTargetLines)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.rows.length === 0 && <div className="text-center py-8 text-slate-500 text-sm">No sales in this period</div>}
            </div>
          </div>

          {/* Lines sold below cost */}
          <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
            <div className="flex items-center justify-between px-4 pt-3 pb-2">
              <h3 className="text-sm font-semibold text-white">Sold below cost <span className="text-slate-500 font-normal">({fmtNum(data.belowCost.length)} lines)</span></h3>
              <div className="flex items-center gap-2">
                <ExportButtons what="the below-cost lines" disabled={data.belowCost.length === 0}
                  onCsv={() => exportCsv('below-cost', BELOW_COLS, data.belowCost)} onXlsx={() => exportXlsx('below-cost', BELOW_COLS, data.belowCost)} />
              </div>
            </div>
            <div className="overflow-x-auto max-h-[50vh] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 z-10 bg-slate-800">
                  <tr className="border-b border-slate-700/50 text-slate-400">
                    <th className="px-3 py-2 text-left font-medium">Date</th>
                    <th className="px-3 py-2 text-left font-medium">Invoice</th>
                    <th className="px-3 py-2 text-left font-medium">Customer</th>
                    <th className="px-3 py-2 text-left font-medium">Salesman</th>
                    <th className="px-3 py-2 text-left font-medium">Item</th>
                    <th className="px-3 py-2 text-right font-medium">Qty</th>
                    <th className="px-3 py-2 text-right font-medium">Price</th>
                    <th className="px-3 py-2 text-right font-medium">Unit Cost</th>
                    <th className="px-3 py-2 text-right font-medium">Ext</th>
                    <th className="px-3 py-2 text-right font-medium">GP</th>
                  </tr>
                </thead>
                <tbody>
                  {data.belowCost.map((r, i) => (
                    <tr key={`${r.invoice}-${r.itemNo}-${i}`} className="border-b border-slate-800/50 hover:bg-slate-700/20">
                      <td className="px-3 py-1.5 text-slate-300 whitespace-nowrap">{r.date}</td>
                      <td className="px-3 py-1.5 text-slate-300">{r.invoice}</td>
                      <td className="px-3 py-1.5 text-white max-w-[200px] truncate" title={r.customerName}>{r.customerName} <span className="text-slate-500">#{r.customerCode}</span></td>
                      <td className="px-3 py-1.5 text-slate-400">{r.salesman}</td>
                      <td className="px-3 py-1.5 text-slate-300 max-w-[240px] truncate" title={r.itemDesc}>{r.itemNo} <span className="text-slate-500">{r.itemDesc}</span></td>
                      <td className="px-3 py-1.5 text-right text-slate-300 tabular-nums">{fmtNum(r.qty)}</td>
                      <td className="px-3 py-1.5 text-right text-slate-200 tabular-nums">{fmtCur2(r.price)}</td>
                      <td className="px-3 py-1.5 text-right text-slate-400 tabular-nums">{fmtCur2(r.unitCost)}</td>
                      <td className="px-3 py-1.5 text-right text-slate-200 tabular-nums">{fmtCur2(r.ext)}</td>
                      <td className="px-3 py-1.5 text-right text-red-300 tabular-nums">{fmtCur2(r.gp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.belowCost.length === 0 && <div className="text-center py-6 text-slate-500 text-sm">Nothing sold below cost in this period</div>}
            </div>
            {data.truncated && (
              <p className="px-4 py-2 text-[11px] text-slate-500 border-t border-slate-800/60">Showing the first lines only — shorten the period to see the rest.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
